import { useCallback, useEffect, useState, type FormEvent } from 'react'
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Ban, Check, Copy, Search, ShieldAlert, ShieldOff, X } from 'lucide-react'
import { billingApi } from '@/lib/billingApi'
import { discoveryApi } from '@/lib/discoveryApi'
import { moderationApi, type ModUserRef, type ModGuildRow, type ModReport, type ModUser, type ModUserRow, type ReportStatus, type ModDecision, isPlatformAdmin, isPlatformStaff } from '@/lib/moderationApi'
import { describeError } from '@/lib/auth'
import { formatDateTime } from '@/lib/format'
import { REPORT_CATEGORIES } from '@/lib/reportApi'
import { cx } from '@/lib/cx'
import { Field } from '@/pages/AuthPage'
import auth from '@/pages/AuthPage.module.css'
import layer from '../settings/SettingsLayer.module.css'
import gs from '../guild/GuildSettingsLayer.module.css'
import styles from './ModerationSection.module.css'
import { decorApi } from '@/lib/decor'
import { Select } from '../Select'
import { Avatar } from '../Avatar'
import { avatarUrl } from '@/lib/avatar'
import { useAppStore } from '@/store/app'
import { useAuthStore } from '@/store/auth'
import { ConfirmModal } from '../guild/ConfirmModal'
import { StatsPage } from '@/pages/stats/StatsPage'

type Tab = 'stats' | 'users' | 'guilds' | 'reports' | 'discovery' | 'log' | 'staff'

const ACTION_LABELS: Record<string, string> = {
  ban: 'заблокировал(а) аккаунт',
  unban: 'снял(а) блокировку',
  restrict: 'ограничил(а) аккаунт',
  unrestrict: 'снял(а) ограничение',
  report_resolved: 'закрыл(а) жалобу с мерами',
  report_rejected: 'отклонил(а) жалобу',
  message_delete: 'удалил(а) сообщение',
  action_link: 'связал(а) жалобу с принятой мерой',
  staff_role: 'изменил(а) роль сотрудника',
}

const TARGET_LABELS: Record<string, string> = { message: 'сообщение', user: 'пользователь', guild: 'сервер' }

function categoryLabel(v: string): string {
  return REPORT_CATEGORIES.find((c) => c.value === v)?.label ?? v
}

/** Separate staff workspace, entered from the profile menu or home sidebar. */
export function ModerationSection({ onClose }: { onClose: () => void }) {
  const admin = useAuthStore(s => isPlatformAdmin(s.user?.flags ?? 0))
  const [tab, setTab] = useState<Tab>('reports')
  const tabs: [Tab, string][] = [['reports', 'Жалобы'], ['users', 'Учётные записи'], ['guilds', 'Серверы'], ['log', 'Журнал']]
  if (admin) tabs.push(['discovery', 'Каталог'], ['stats', 'Статистика'], ['staff', 'Команда'])
  const current = tabs.some(([id]) => id === tab) ? tab : 'reports'
  return <section className={styles.workspace} aria-label="Модерация">
    <header className={styles.workspaceHead}><ShieldAlert size={24} /><h1>Модерация</h1><span className={styles.role}>{admin ? 'Администратор' : 'Модератор'}</span><button type="button" onClick={onClose} aria-label="Закрыть модерацию"><X size={24} /></button></header>
    <nav className={styles.workspaceNav} aria-label="Разделы модерации">{tabs.map(([id, label]) => <button key={id} type="button" aria-current={current === id ? 'page' : undefined} className={cx(styles.workspaceLink, current === id && styles.tabActive)} onClick={() => setTab(id)}>{label}</button>)}</nav>
    <div className={cx(styles.workspaceContent, 'scroller')} key={current}>
      <h2 className={styles.pageTitle}>{tabs.find(([id]) => id === current)?.[1]}</h2>
      {current === 'stats' ? <StatsPage embedded /> : current === 'reports' ? <ReportsQueue /> : current === 'users' ? <UsersTab /> : current === 'guilds' ? <GuildsTab /> : current === 'discovery' ? <DiscoveryQueue /> : current === 'staff' ? <StaffTeam /> : <ActionLog />}
    </div>
  </section>
}

const num = (n: number) => n.toLocaleString('ru-RU')

function ReportsQueue() {
  const [selectedUser, setSelectedUser] = useState<string | null>(null)
  const [selected, setSelected] = useState<string | null>(null)
  const [status, setStatus] = useState<ReportStatus>('open')
  const reports = useInfiniteQuery({ queryKey: ['mod-reports', status], queryFn: ({ pageParam }) => moderationApi.reports(status, pageParam), initialPageParam: undefined as string | undefined, getNextPageParam: (page) => page.length === 50 ? page.at(-1)?.id : undefined, refetchInterval: 30_000 })
  const list = reports.data?.pages.flat() ?? []
  const r = list.find(r => r.id === selected) ?? list[0]
  const activeId = r?.id
  // Keep the opened case when a new report arrives during a decision draft.
  if (activeId && activeId !== selected) setSelected(activeId)
  if (selectedUser) return <UserLookup initialId={selectedUser} onBack={() => setSelectedUser(null)} />
  return <>
    <div className={styles.filters}>{(['open', 'resolved', 'rejected'] as const).map(s => <button key={s} type="button" className={cx(gs.smallButton, status === s && styles.filterActive)} onClick={() => { setStatus(s); setSelected(null) }}>{s === 'open' ? 'Открытые' : s === 'resolved' ? 'С мерами' : 'Отклонённые'}</button>)}</div>
    {reports.isPending ? <p className={layer.text}>Загружаем…</p> : null}
    {reports.isError ? <p className={gs.error} role="alert">{describeError(reports.error).message}</p> : null}
    {!reports.isPending && !reports.isError && !list.length ? <div className={styles.emptyQueue}><Check size={28} /><p>Жалоб нет</p></div> : null}
    {r ? <div className={styles.caseGrid}>
      <div className={styles.caseList} aria-label="Очередь жалоб">{list.map(item => <button key={item.id} type="button" className={cx(styles.caseItem, r.id === item.id && styles.caseSelected)} aria-pressed={r.id === item.id} onClick={() => setSelected(item.id)}><strong>{categoryLabel(item.category)}</strong><span>{item.target_type === 'guild' ? item.guild_name ?? 'Сервер' : snapshotUser(item.target_type === 'user' ? item.snapshot : item.snapshot.author)?.name ?? TARGET_LABELS[item.target_type]}</span><small>{formatDateTime(new Date(item.created_at))}</small></button>)}
      {reports.hasNextPage ? <button type="button" className={gs.smallButton} disabled={reports.isFetchingNextPage} onClick={() => { void reports.fetchNextPage() }}>Показать ещё</button> : null}</div>
      <ReportCase key={r.id} report={r} onUser={setSelectedUser} />
    </div> : null}
  </>
}

function ReportCase({ report: r, onUser }: { report: ModReport; onUser: (id: string) => void }) {
  const snap = r.snapshot
  const author = snapshotUser(r.target_type === 'user' ? snap : snap.author)
  const authorId = author?.id ?? (typeof snap.author_id === 'string' ? snap.author_id : r.target_type === 'user' ? r.target_id : null)
  return <article className={styles.caseDetail}>
    <div className={styles.reportHead}><span className={cx(styles.category, ['csam', 'violence', 'extremism'].includes(r.category) && styles.categorySevere)}>{categoryLabel(r.category)}</span><span className={styles.meta}>{TARGET_LABELS[r.target_type]} · {formatDateTime(new Date(r.created_at))}</span><CopyID id={r.id} label="ID жалобы" /></div>
    <div className={styles.identityRow}>{authorId ? <Identity user={author} id={authorId} onOpen={onUser} /> : <strong>{typeof snap.name === 'string' ? snap.name : 'Сервер недоступен'}</strong>}<CopyID id={r.target_id} label={r.target_type === 'message' ? 'ID сообщения' : 'ID объекта'} /></div>
    <div className={styles.identityRow}><span className={styles.meta}>Жалоба от</span><Identity user={r.reporter} id={r.reporter_id} onOpen={onUser} /></div>
    {r.guild_name || r.channel_name ? <p className={styles.meta}>{r.guild_name ?? 'Личные сообщения'}{r.channel_name ? ` · #${r.channel_name}` : ''}</p> : null}
    {r.channel_id && r.target_type === 'message' ? <button type="button" className={gs.smallButton} onClick={() => { useAppStore.getState().closeModeration(); openReportMessage(r) }}>Перейти к сообщению</button> : null}
    {r.text ? <p className={styles.reportText}>{r.text}</p> : null}
    <h3 className={styles.blockTitle}>Снимок при подаче жалобы</h3><pre className={styles.snapshot}>{snapshotText(snap)}</pre>
    {r.status === 'open' ? <CaseDecision report={r} authorId={authorId} /> : <div className={styles.decisionResult}><strong>{DECISION_LABELS[r.decision ?? ''] ?? (r.status === 'resolved' ? 'Меры приняты' : 'Жалоба отклонена')}</strong><p>{r.resolution}</p><span className={styles.meta}>{r.resolver?.name ?? 'Сотрудник'} · {r.resolved_at ? formatDateTime(new Date(r.resolved_at)) : ''}{r.decision_until ? ` · до ${formatDateTime(new Date(r.decision_until))}` : ''}</span></div>}
    <CaseHistory id={r.id} />
  </article>
}

const DECISION_LABELS: Record<string, string> = { reject: 'Нарушения нет', message_delete: 'Сообщение удалено', ban: 'Учётная запись заблокирована', restrict: 'Заявки и приглашения ограничены', link: 'Связана с принятой мерой' }
function CaseDecision({ report, authorId }: { report: ModReport; authorId: string | null }) {
  const admin = useAuthStore(s => isPlatformAdmin(s.user?.flags ?? 0))
  const qc = useQueryClient()
  const [action, setAction] = useState<ModDecision['action']>('reject')
  const [reason, setReason] = useState('')
  const [days, setDays] = useState('7')
  const [linked, setLinked] = useState('')
  const prior = useQuery({ queryKey: ['mod-user-actions', authorId], queryFn: () => moderationApi.userActions(authorId!), enabled: action === 'link' && !!authorId })
  const eligible = (prior.data ?? []).filter(a => ['ban', 'restrict'].includes(a.action) || (a.action === 'message_delete' && a.target_id === report.target_id))
  const decision = useMutation({ mutationFn: (input: ModDecision) => moderationApi.decide(report.id, input), onSuccess: async () => { await Promise.all([qc.invalidateQueries({ queryKey: ['mod-reports'] }), qc.invalidateQueries({ queryKey: ['mod-actions'] }), qc.invalidateQueries({ queryKey: ['mod-case-actions', report.id] }), qc.invalidateQueries({ queryKey: ['mod-user-actions'] })]) } })
  const options = [{ value: 'reject', label: 'Нарушения нет' }, ...(report.target_type === 'message' && report.channel_id ? [{ value: 'message_delete', label: 'Удалить сообщение' }] : []), ...(authorId ? [{ value: 'ban', label: 'Заблокировать учётную запись' }, { value: 'restrict', label: 'Ограничить заявки и приглашения' }, { value: 'link', label: 'Связать с принятой мерой' }] : [])]
  const submit = (e: FormEvent) => {
    e.preventDefault()
    const input: ModDecision = { action, reason: reason.trim() }
    if (action === 'ban' || action === 'restrict') input.until = days === 'forever' ? null : new Date(Date.now() + Number(days) * 86400000).toISOString()
    if (action === 'link') input.action_id = linked
    decision.mutate(input)
  }
  const durations = [1, 3, 7, ...(admin ? [14, 30, ...(action === 'restrict' ? [90] : [90, 365])] : [])].map(n => ({ value: String(n), label: `${n} ${n === 1 ? 'день' : n === 3 ? 'дня' : 'дней'}` }))
  if (admin && action === 'ban') durations.push({ value: 'forever', label: 'Бессрочно' })
  return <form className={styles.decisionForm} onSubmit={submit}>
    <h3 className={styles.blockTitle}>Решение</h3>
    <Select value={action} options={options} onChange={v => { setAction(v as ModDecision['action']); setDays('7'); decision.reset() }} label="Решение по жалобе" />
    {action === 'ban' || action === 'restrict' ? <Select value={days} options={durations} onChange={setDays} label="Срок меры" /> : null}
    {action === 'restrict' ? <p className={styles.meta}>Заявки в друзья и приглашения отключены. Сообщения и голос доступны.</p> : null}
    {action === 'link' ? <><Select value={linked} options={eligible.map(a => ({ value: a.id, label: `${a.action === 'ban' ? 'Блокировка' : a.action === 'restrict' ? 'Ограничение' : 'Удаление сообщения'} · ${formatDateTime(new Date(a.created_at))} · ${a.staff?.name ?? 'Сотрудник'}` }))} onChange={setLinked} label="Принятая мера" />{prior.isPending ? <p className={styles.meta}>Загружаем меры…</p> : prior.isError ? <p className={gs.error}>{describeError(prior.error).message}</p> : !eligible.length ? <p className={styles.meta}>Подходящих мер нет</p> : null}</> : null}
    <label className={styles.reasonLabel} htmlFor={`case-reason-${report.id}`}>Причина</label><textarea id={`case-reason-${report.id}`} className={auth.input} rows={3} required maxLength={500} value={reason} onChange={e => setReason(e.target.value)} placeholder="Почему принято это решение" />
    {decision.isError ? <p className={gs.error} role="alert">{describeError(decision.error).message}</p> : null}
    <button type="submit" className={cx(layer.button, action !== 'reject' && gs.smallDanger)} disabled={decision.isPending || !reason.trim() || (action === 'link' && !linked)}>{decision.isPending ? 'Применяем…' : action === 'reject' ? 'Отклонить жалобу' : 'Применить и закрыть жалобу'}</button>
  </form>
}
function CaseHistory({ id }: { id: string }) {
  const history = useQuery({ queryKey: ['mod-case-actions', id], queryFn: () => moderationApi.reportActions(id) })
  return <section><h3 className={styles.blockTitle}>История дела</h3>{history.isPending ? <p className={styles.meta}>Загружаем…</p> : history.isError ? <p className={gs.error}>{describeError(history.error).message}</p> : !history.data?.length ? <p className={styles.meta}>Решение ещё не принято</p> : <ol className={styles.history}>{history.data.map(a => <li key={a.id}><strong>{a.staff?.name ?? 'Сотрудник'}</strong> {ACTION_LABELS[a.action] ?? a.action}<p>{a.reason}</p><small>{formatDateTime(new Date(a.created_at))}{typeof a.meta.until === 'string' ? ` · до ${formatDateTime(new Date(a.meta.until))}` : ''}</small>{typeof a.meta.linked_action_id === 'string' ? <CopyID id={a.meta.linked_action_id} label="ID связанной меры" /> : null}</li>)}</ol>}</section>
}

function StaffTeam() {
  const qc = useQueryClient()
  const me = useAuthStore(s => s.user?.id)
  const staff = useQuery({ queryKey: ['mod-staff'], queryFn: moderationApi.staff })
  const [query, setQuery] = useState('')
  const [found, setFound] = useState<ModUser | null>(null)
  const [role, setRole] = useState('moderator')
  const [confirm, setConfirm] = useState<{ id: string; name: string; role: 'admin' | 'moderator' | 'none' } | null>(null)
  const [error, setError] = useState('')
  const lookup = useMutation({ mutationFn: () => moderationApi.findUser(query.trim()), onSuccess: setFound, onError: e => setError(describeError(e).message) })
  const change = useMutation({ mutationFn: ({ id, role }: { id: string; role: 'admin' | 'moderator' | 'none' }) => moderationApi.setRole(id, role), onSuccess: async () => { setFound(null); setQuery(''); await qc.invalidateQueries({ queryKey: ['mod-staff'] }) } })
  const roles = [{ value: 'moderator', label: 'Модератор' }, { value: 'admin', label: 'Администратор' }, { value: 'none', label: 'Снять доступ' }]
  return <>
    {confirm ? <ConfirmModal title="Доступ к модерации" text={`@${confirm.name}: ${roles.find(r => r.value === confirm.role)?.label}`} confirmLabel="Подтвердить" onCancel={() => setConfirm(null)} onConfirm={async () => { await change.mutateAsync(confirm); setConfirm(null) }} /> : null}
    <p className={layer.text}>Модератор разбирает жалобы, удаляет сообщения и применяет меры до 7 дней.</p>
    {staff.isError ? <p className={gs.error}>{describeError(staff.error).message}</p> : null}
    <p className={layer.text}>Администратор также управляет командой, выдаёт подписки и блокирует бессрочно.</p>
    <ul className={styles.list}>{staff.data?.map(member => <li className={styles.report} key={member.user.id}><strong>{member.user.global_name ?? member.user.username} <span className={styles.meta}>@{member.user.username}</span></strong>{member.user.id === me ? <span className={styles.meta}>Администратор · вы</span> : <Select value={member.role} options={roles} onChange={role => setConfirm({ id: member.user.id, name: member.user.username, role: role as 'admin' | 'moderator' | 'none' })} label={`Роль @${member.user.username}`} disabled={change.isPending} />}</li>)}</ul>
    <h3 className={styles.blockTitle}>Добавить сотрудника</h3><form className={styles.search} onSubmit={e => { e.preventDefault(); setError(''); setFound(null); lookup.mutate() }}><input className={auth.input} aria-label="Имя сотрудника" placeholder="@имя" value={query} onChange={e => setQuery(e.target.value)} /><button className={layer.button} disabled={lookup.isPending || !query.trim()}>Найти</button></form>
    {error ? <p className={gs.error}>{error}</p> : null}{change.isError ? <p className={gs.error} role="alert">{describeError(change.error).message}</p> : null}
    {found ? <div className={styles.card}><strong>{found.user.global_name ?? found.user.username} · @{found.user.username}</strong><Select value={role} options={roles.slice(0,2)} onChange={setRole} label="Роль сотрудника" /><button type="button" className={layer.button} disabled={change.isPending || found.user.id === me || !!found.user.bot} onClick={() => setConfirm({ id: found.user.id, name: found.user.username, role: role as 'admin' | 'moderator' })}>Выдать доступ</button></div> : null}
  </>
}

function snapshotText(snap: Record<string, unknown>): string {
  const parts: string[] = []
  if (typeof snap.content === 'string' && snap.content) parts.push(snap.content)
  if (typeof snap.username === 'string') parts.push(`@${snap.username}`)
  if (typeof snap.name === 'string') parts.push(snap.name)
  if (typeof snap.description === 'string' && snap.description) parts.push(snap.description)
  if (Array.isArray(snap.attachments) && snap.attachments.length) parts.push(`вложений: ${snap.attachments.length}`)
  return parts.join('\n') || (typeof snap.embeds === 'number' && snap.embeds > 0 ? `Встроенных карточек: ${snap.embeds}` : 'Без текста')
}

function UserLookup({ initialId, onBack }: { initialId?: string; onBack?: () => void } = {}) {
  const qc = useQueryClient()
  const admin = useAuthStore(s => isPlatformAdmin(s.user?.flags ?? 0))
  const me = useAuthStore(s => s.user?.id)
  const [query, setQuery] = useState(initialId ?? '')
  const [card, setCard] = useState<ModUser | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [reason, setReason] = useState('')
  const [days, setDays] = useState(7)
  const [busy, setBusy] = useState(false)
  const [granted, setGranted] = useState<string | null>(null)
  const [decorItem, setDecorItem] = useState('frame:klich')
  const decorations = useQuery({ queryKey: ['decorations'], queryFn: decorApi.inventory })
  const decorOptions = [
    ...(decorations.data?.frames ?? []).map((f) => ({ key: `frame:${f.id}`, label: `Рамка «${f.name}»` })),
    ...(decorations.data?.effects ?? []).map((f) => ({ key: `effect:${f.id}`, label: `Эффект «${f.name}»` })),
    { key: 'name:neon', label: 'Эффект ника «Неон»' },
  ]

  // ручная выдача «Плюса» и бустов до подключения кассы
  const grant = async (fn: () => Promise<unknown>, done = 'Выдано') => {
    setBusy(true)
    setError(null)
    try {
      await fn()
      setGranted(done)
    } catch (err) {
      setError(describeError(err).message)
    } finally {
      setBusy(false)
    }
  }

  const run = useCallback(async (fn: () => Promise<ModUser>) => {
    setBusy(true)
    setError(null)
    try {
      const next = await fn()
      setCard(next)
      void qc.invalidateQueries({ queryKey: ['mod-user-actions', next.user.id] })
    } catch (err) {
      setError(describeError(err).message)
    } finally {
      setBusy(false)
    }
  }, [qc])
  // из списка карточка открывается сразу, без нажатия «Найти»
  useEffect(() => {
    if (initialId) void run(() => moderationApi.user(initialId))
  }, [initialId, run])

  const search = (e: FormEvent) => {
    e.preventDefault()
    const q = query.trim()
    if (!q) return
    void run(() => (/^\d{15,20}$/.test(q) ? moderationApi.user(q) : moderationApi.findUser(q)))
  }
  const until = () => new Date(Date.now() + days * 86400000).toISOString()

  return (
    <>
      {onBack ? <p><button type="button" className={cx(layer.button, layer.buttonSecondary)} onClick={onBack}>← К списку</button></p> : null}
      <form className={styles.search} onSubmit={search}>
        <input className={auth.input} placeholder="@имя или id" value={query} onChange={(e) => setQuery(e.target.value)} aria-label="Поиск аккаунта" />
        <button type="submit" className={layer.button} disabled={busy}><Search size={16} /> Найти</button>
      </form>
      {error ? <p className={gs.error} role="alert">{error}</p> : null}
      {card ? (
        <div className={styles.card}>
          <div className={styles.cardHead}>
            <strong>{card.user.global_name ?? card.user.username}</strong> <span className={styles.meta}>@{card.user.username} · {card.user.id} · с {formatDateTime(new Date(card.created_at))}</span>
          </div>
          <p className={styles.meta}>Серверов: {card.guilds} · жалоб на аккаунт: {card.reports_against}{isPlatformStaff(card.flags) ? ' · сотрудник' : ''}</p>
          {card.banned ? (
            <p className={styles.banned}>Заблокирован{card.banned.until ? ` до ${formatDateTime(new Date(card.banned.until))}` : ' бессрочно'}{card.banned.reason ? `: ${card.banned.reason}` : ''}</p>
          ) : null}
          {card.restricted ? <p className={styles.restricted}>Ограничен до {formatDateTime(new Date(card.restricted.until))}{card.restricted.reason ? `: ${card.restricted.reason}` : ''}</p> : null}
          {card.username_history?.length ? (
            <details>
              <summary>История имён пользователя</summary>
              <ul className={gs.table}>
                {card.username_history.map((change) => (
                  <li key={change.changed_at} className={gs.tableRow}>
                    <div className={gs.tableMain}>
                      <span>@{change.old_username} → @{change.new_username}</span>
                      <span className={gs.tableMeta}>{formatDateTime(new Date(change.changed_at))}</span>
                    </div>
                  </li>
                ))}
              </ul>
              {card.username_history.length === 50 ? <p className={styles.meta}>Последние 50 смен</p> : null}
            </details>
          ) : null}
          <Field label="Причина" htmlFor="mod-reason" hint="Видна в журнале и заблокированному при входе">
            <input id="mod-reason" className={auth.input} value={reason} maxLength={500} onChange={(e) => setReason(e.target.value)} />
          </Field>
          <Field label="Срок, дней" htmlFor="mod-days" hint="Для блокировки на срок и ограничения">
            <input id="mod-days" type="number" min={1} max={admin ? 3650 : 7} className={auth.input} value={days} onChange={(e) => setDays(Number(e.target.value))} />
          </Field>
          <div className={styles.actions}>
            {card.banned ? (
              <button hidden={!admin} type="button" className={gs.smallButton} disabled={busy} onClick={() => void run(() => moderationApi.unban(card.user.id))}>Снять блокировку</button>
            ) : (
              <>
                <button type="button" className={cx(gs.smallButton, gs.smallDanger)} disabled={busy || !reason.trim() || card.user.id === me || isPlatformAdmin(card.flags) || (!admin && isPlatformStaff(card.flags))} onClick={() => void run(() => moderationApi.ban(card.user.id, { reason, until: until() }))}><Ban size={14} /> Заблокировать на срок</button>
                <button hidden={!admin} type="button" className={cx(gs.smallButton, gs.smallDanger)} disabled={busy || !reason.trim() || card.user.id === me || isPlatformAdmin(card.flags) || (!admin && isPlatformStaff(card.flags))} onClick={() => void run(() => moderationApi.ban(card.user.id, { reason, until: null }))}><Ban size={14} /> Заблокировать навсегда</button>
              </>
            )}
            {card.restricted ? (
              <button hidden={!admin} type="button" className={gs.smallButton} disabled={busy} onClick={() => void run(() => moderationApi.unrestrict(card.user.id))}>Снять ограничение</button>
            ) : (
              <button type="button" className={gs.smallButton} disabled={busy || !reason.trim() || card.user.id === me || isPlatformAdmin(card.flags) || (!admin && isPlatformStaff(card.flags))} onClick={() => void run(() => moderationApi.restrict(card.user.id, { reason, until: until() }))}><ShieldOff size={14} /> Ограничить заявки и приглашения</button>
            )}
          </div>
          <p className={layer.text}>Блокировка завершает все сеансы и запрещает вход. Ограничение отключает заявки в друзья и приглашения.</p>
          {admin ? <>
          <div className={styles.actions}>
            <button type="button" className={gs.smallButton} disabled={busy} onClick={() => void grant(() => billingApi.grantPlus(card.user.id, 1))}>Выдать «Плюс» на месяц</button>
            <button type="button" className={gs.smallButton} disabled={busy} onClick={() => void grant(() => billingApi.grantBoosts(card.user.id, 2, 1))}>Выдать 2 буста</button>
          </div>
          {/* украшения навсегда: призы, компенсации и проверка закрытого на тестовом сервере */}
          <div className={styles.actions}>
            <Select className={styles.decorSelect} value={decorItem} options={decorOptions.map((o) => ({ value: o.key, label: o.label }))} onChange={setDecorItem} label="Украшение" />
            <button type="button" className={gs.smallButton} disabled={busy} onClick={() => void grant(() => decorApi.grant(card.user.id, decorItem))}>Выдать украшение</button>
            <button type="button" className={gs.smallButton} disabled={busy} onClick={() => void grant(() => decorApi.revoke(card.user.id, decorItem), 'Забрано')}>Забрать</button>
          </div>
          </> : null}
          {granted ? <p className={layer.text}>{granted}</p> : null}
          <UserActionHistory id={card.user.id} />
        </div>
      ) : null}
    </>
  )
}

function ActionLog() {
  const [selectedUser, setSelectedUser] = useState<string | null>(null)
  const actions = useInfiniteQuery({ queryKey: ['mod-actions'], queryFn: ({ pageParam }) => moderationApi.actions(pageParam), initialPageParam: undefined as string | undefined, getNextPageParam: (page) => page.length === 50 ? page.at(-1)?.id : undefined })
  const list = actions.data?.pages.flat() ?? []
  if (selectedUser) return <UserLookup initialId={selectedUser} onBack={() => setSelectedUser(null)} />
  return (
    <>
      {actions.isPending ? <p className={layer.text}>Загружаем…</p> : null}
      {actions.isError ? <p className={gs.error} role="alert">{describeError(actions.error).message}</p> : null}
      {!actions.isPending && !actions.isError && list.length === 0 ? <p className={layer.text}>Действий пока нет.</p> : null}
      <ul className={gs.table}>
        {list.map((a) => (
          <li key={a.id} className={gs.tableRow}>
            <div className={gs.tableMain}>
              <span className={gs.auditLine}><Identity user={a.staff} id={a.staff_id} onOpen={setSelectedUser} /> <span className={gs.auditAction}>{ACTION_LABELS[a.action] ?? a.action}</span> {a.target_user_id ? <Identity user={a.target_user} id={a.target_user_id} onOpen={setSelectedUser} /> : a.target_id ? <CopyID id={a.target_id} label={a.action === 'message_delete' ? 'ID сообщения' : 'ID жалобы'} /> : null}</span>
              {a.action === 'message_delete' ? <><pre className={styles.snapshot}>{typeof a.meta.content === 'string' && a.meta.content ? a.meta.content : a.meta.snapshot && typeof a.meta.snapshot === 'object' ? snapshotText(a.meta.snapshot as Record<string, unknown>) : 'Сообщение удалено · текст не сохранён'}</pre>{a.target_id ? <CopyID id={a.target_id} label="ID сообщения" /> : null}</> : a.meta.snapshot && typeof a.meta.snapshot === 'object' ? <pre className={styles.snapshot}>{snapshotText(a.meta.snapshot as Record<string, unknown>)}</pre> : null}
              <span className={gs.tableMeta}>{formatDateTime(new Date(a.created_at))}{a.reason ? ` · ${a.reason}` : ''}{typeof a.meta.until === 'string' ? ` · до ${formatDateTime(new Date(a.meta.until))}` : ''}</span>
            </div>
          </li>
        ))}
      </ul>
      {actions.hasNextPage ? <button type="button" className={gs.smallButton} disabled={actions.isFetchingNextPage} onClick={() => { void actions.fetchNextPage() }}>Показать ещё</button> : null}
    </>
  )
}

/** Очередь каталога: карточки публичных серверов, скрытие и возврат. */
function DiscoveryQueue() {
  const qc = useQueryClient()
  const [state, setState] = useState('')
  const list = useQuery({ queryKey: ['mod-discovery', state], queryFn: () => discoveryApi.staff(state || undefined) })
  const [reason, setReason] = useState('')
  const change = useMutation({
    mutationFn: ({ guildId, next }: { guildId: string; next: string }) => discoveryApi.setState(guildId, next, reason),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['mod-discovery'] }),
  })
  const guilds = list.data?.guilds ?? []

  return (
    <>
      <div className={styles.filters}>
        {[
          ['', 'Все'],
          ['listed', 'В каталоге'],
          ['auto_hidden', 'Скрытые'],
          ['removed', 'Снятые'],
        ].map(([id, label]) => (
          <button key={id} type="button" className={cx(gs.smallButton, state === id && styles.filterActive)} onClick={() => setState(id ?? '')}>{label}</button>
        ))}
      </div>
      <Field label="Причина" htmlFor="disc-reason" hint="Видна владельцу сервера на вкладке «Каталог»">
        <input id="disc-reason" className={auth.input} value={reason} maxLength={200} onChange={(e) => setReason(e.target.value)} />
      </Field>
      {guilds.length === 0 ? <p className={layer.text}>Пусто.</p> : null}
      {guilds.map((g) => (
        <div key={g.guild_id} className={styles.card}>
          <div className={styles.cardHead}>
            <strong>{g.name}</strong> <span className={styles.meta}>{g.guild_id} · {g.category} · {g.approximate_member_count} участников · {g.approximate_presence_count} онлайн · жалоб: {g.reports_open ?? 0} · {g.state}</span>
          </div>
          <p className={styles.meta}>{g.description}</p>
          <div className={styles.actions}>
            {g.state !== 'listed' ? (
              <button type="button" className={gs.smallButton} onClick={() => change.mutate({ guildId: g.guild_id, next: 'listed' })}>Вернуть в каталог</button>
            ) : (
              <button type="button" className={gs.smallButton} onClick={() => change.mutate({ guildId: g.guild_id, next: 'auto_hidden' })}>Скрыть до разбора</button>
            )}
            <button type="button" className={cx(gs.smallButton, gs.smallDanger)} onClick={() => change.mutate({ guildId: g.guild_id, next: 'removed' })}>Снять навсегда</button>
          </div>
        </div>
      ))}
    </>
  )
}

/** Дата коротко: сегодня — время, иначе число. */
function shortDate(iso: string | null): string {
  if (!iso) return '—'
  const d = new Date(iso)
  return d.toDateString() === new Date().toDateString()
    ? d.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })
    : d.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: '2-digit' })
}

const STATUS_LABEL: Record<ModUserRow['status'], string> = { online: 'в сети', idle: 'отошёл', dnd: 'не беспокоить', offline: 'не в сети' }

/*
 * Все учётные записи одним списком: новые сверху, поиск по началу ника. Клик по строке
 * открывает ту же карточку с действиями, что и поиск. Колонки — то, по чему видно живого
 * человека: когда был, чем подтверждён, где состоит.
 */
function UsersTab() {
  const [q, setQ] = useState('')
  const [applied, setApplied] = useState('')
  // люди и боты — разные списки: бот — учётная запись приложения, а не человек
  const [bots, setBots] = useState(false)
  const [pages, setPages] = useState<ModUserRow[][]>([])
  const [selected, setSelected] = useState<string | null>(null)
  const first = useQuery({ queryKey: ['mod-users', applied, bots], queryFn: () => moderationApi.users({ q: applied || undefined, bots }) })
  const rows = [...(first.data?.users ?? []), ...pages.flat()]
  const last = rows[rows.length - 1]
  const more = useMutation({
    mutationFn: () => moderationApi.users({ q: applied || undefined, before: last?.id, bots }),
    onSuccess: (res) => setPages((p) => [...p, res.users]),
  })
  if (selected) return <UserLookup initialId={selected} onBack={() => setSelected(null)} />
  return (
    <div>
      <div className={styles.filters} role="group" aria-label="Кого показать">
        {([[false, 'Люди', first.data?.people], [true, 'Боты', first.data?.bots]] as const).map(([value, label, n]) => (
          <button key={label} type="button" className={cx(gs.smallButton, bots === value && styles.filterActive)} aria-pressed={bots === value} onClick={() => { setBots(value); setPages([]) }}>
            {n !== undefined ? `${label} · ${num(n)}` : label}
          </button>
        ))}
      </div>
      <form className={styles.search} onSubmit={(e) => { e.preventDefault(); setPages([]); setApplied(q.trim()) }}>
        <input className={auth.input} placeholder="Начало ника или имени" value={q} onChange={(e) => setQ(e.target.value)} />
        <button type="submit" className={layer.button}>Найти</button>
        {applied ? <button type="button" className={cx(layer.button, layer.buttonSecondary)} onClick={() => { setQ(''); setApplied(''); setPages([]) }}>Все</button> : null}
      </form>
      <p className={styles.meta}>{first.data ? `Всего: ${first.data.total}` : first.isPending ? 'Загружаем…' : 'Не удалось загрузить'}</p>
      <div className={styles.table} role="table">
        <div className={cx(styles.tr, styles.th)} role="row">
          <span>Учётная запись</span><span>Была</span><span>Вход</span><span>Серверов</span><span>Статус</span>
        </div>
        {rows.map((u) => (
          <button key={u.id} type="button" className={styles.tr} role="row" onClick={() => setSelected(u.id)}>
            <span>
              <strong>{u.display_name ?? u.username}</strong> <span className={styles.meta}>@{u.username} · {shortDate(u.created_at)}{u.bot_owner ? ` · владелец @${u.bot_owner}` : ''}</span>
            </span>
            <span className={styles.meta}>{u.status !== 'offline' ? STATUS_LABEL[u.status] : shortDate(u.last_seen)}</span>
            <span className={styles.meta}>{[u.has_phone ? 'номер' : null, u.has_email ? 'почта' : null].filter(Boolean).join(' + ') || '—'}</span>
            <span className={styles.meta}>{u.guilds}{u.owned ? ` (свои: ${u.owned})` : ''}</span>
            <span className={styles.meta}>
              {[
                u.banned ? 'бан' : null,
                u.restricted ? 'ограничен' : null,
                u.deleting ? 'удаляется' : null,
                u.plus ? 'Плюс' : null,
                (u.flags & 1) !== 0 ? 'сотрудник' : null,
              ].filter(Boolean).join(', ') || '—'}
            </span>
          </button>
        ))}
      </div>
      {rows.length > 0 && rows.length % 50 === 0 ? (
        <button type="button" className={cx(layer.button, layer.buttonSecondary)} disabled={more.isPending} onClick={() => more.mutate()}>Показать ещё</button>
      ) : null}
    </div>
  )
}

/** Все серверы: кто владеет, сколько людей, живой ли (сообщения за неделю), в каталоге ли. */
function GuildsTab() {
  const [q, setQ] = useState('')
  const [applied, setApplied] = useState('')
  const [pages, setPages] = useState<ModGuildRow[][]>([])
  const first = useQuery({ queryKey: ['mod-guilds', applied], queryFn: () => moderationApi.guilds({ q: applied || undefined }) })
  const rows = [...(first.data?.guilds ?? []), ...pages.flat()]
  const last = rows[rows.length - 1]
  const more = useMutation({
    mutationFn: () => moderationApi.guilds({ q: applied || undefined, before: last?.id }),
    onSuccess: (res) => setPages((p) => [...p, res.guilds]),
  })
  return (
    <div>
      <form className={styles.search} onSubmit={(e) => { e.preventDefault(); setPages([]); setApplied(q.trim()) }}>
        <input className={auth.input} placeholder="Часть названия" value={q} onChange={(e) => setQ(e.target.value)} />
        <button type="submit" className={layer.button}>Найти</button>
        {applied ? <button type="button" className={cx(layer.button, layer.buttonSecondary)} onClick={() => { setQ(''); setApplied(''); setPages([]) }}>Все</button> : null}
      </form>
      <p className={styles.meta}>{first.data ? `Всего: ${first.data.total}` : first.isPending ? 'Загружаем…' : 'Не удалось загрузить'}</p>
      <div className={styles.table} role="table">
        <div className={cx(styles.tr, styles.th)} role="row">
          <span>Сервер</span><span>Владелец</span><span>Участников</span><span>За неделю</span><span>Уровень</span>
        </div>
        {rows.map((g) => (
          <div key={g.id} className={styles.tr} role="row">
            <span>
              <strong>{g.name}</strong> <span className={styles.meta}>{shortDate(g.created_at)}{g.listed ? ' · в каталоге' : ''}</span>
            </span>
            <span className={styles.meta}>@{g.owner_username}</span>
            <span className={styles.meta}>{g.members}{g.bots ? ` · ботов ${g.bots}` : ''}</span>
            <span className={styles.meta}>{g.messages_week} сообщ.</span>
            <span className={styles.meta}>{g.tier ? `${g.tier} (бустов: ${g.boosts})` : '—'}</span>
          </div>
        ))}
      </div>
      {rows.length > 0 && rows.length % 50 === 0 ? (
        <button type="button" className={cx(layer.button, layer.buttonSecondary)} disabled={more.isPending} onClick={() => more.mutate()}>Показать ещё</button>
      ) : null}
    </div>
  )
}

function snapshotUser(value: unknown): ModUserRef | null {
  if (!value || typeof value !== 'object') return null
  const u = value as Record<string, unknown>
  if (typeof u.id !== 'string' || typeof u.username !== 'string') return null
  return { id: u.id, username: u.username, name: typeof u.global_name === 'string' && u.global_name ? u.global_name : u.username, avatar: typeof u.avatar === 'string' ? u.avatar : null }
}
function Identity({ user, id, onOpen }: { user: ModUserRef | null; id: string; onOpen: (id: string) => void }) {
  const name = user?.name ?? 'Удалённый или недоступный аккаунт'
  return <button type="button" className={styles.identity} data-tooltip={user ? `@${user.username}` : id} onClick={() => onOpen(id)}><Avatar initial={name[0] ?? '?'} color="var(--bg-tertiary)" size={24} src={avatarUrl(id, user?.avatar)} /><span>{name}{' '}{user && user.name !== user.username ? <small>@{user.username}</small> : null}</span></button>
}
function CopyID({ id, label }: { id: string; label: string }) {
  const [state, setState] = useState<'idle' | 'copied' | 'failed'>('idle')
  return <button type="button" className={styles.copyID} data-tooltip={`${label}: ${id}`} aria-label={`Скопировать ${label}: ${id}`} onClick={() => { void navigator.clipboard.writeText(id).then(() => setState('copied'), () => setState('failed')) }}>{state === 'copied' ? <Check size={13} /> : <Copy size={13} />}{state === 'copied' ? 'Скопировано' : state === 'failed' ? id : label}</button>
}
function openReportMessage(r: ModReport) {
  if (!r.channel_id) return
  const app = useAppStore.getState()
  if (r.guild_id) app.openGuild(r.guild_id, r.channel_id)
  else app.setView({ kind: 'home', channelId: r.channel_id })
  app.setJumpTarget({ channelId: r.channel_id, messageId: r.target_id })
  app.closeSettings()
}

function UserActionHistory({ id }: { id: string }) {
  const actions = useQuery({ queryKey: ['mod-user-actions', id], queryFn: () => moderationApi.userActions(id) })
  return <section><h3 className={styles.blockTitle}>История мер</h3>{actions.isError ? <p className={gs.error}>{describeError(actions.error).message}</p> : <ol className={styles.history}>{actions.data?.map(a => <li key={a.id}><strong>{a.staff?.name ?? 'Сотрудник'}</strong> {ACTION_LABELS[a.action] ?? a.action}<p>{a.reason}</p><small>{formatDateTime(new Date(a.created_at))}</small>{a.report_id ? <CopyID id={a.report_id} label="ID жалобы" /> : null}</li>)}</ol>}</section>
}
